import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtDate, gstInclusive, parsePrice, shareImages, sharePdf } from "@/lib/pgt";
import { Loader2, Plus, Search, Copy, Download, Share2, Package, Pencil, FileText, UploadCloud, Trash2, ExternalLink, Sparkles, Camera, Image as ImageIcon, Send, User, CheckCircle2, ChevronDown, CheckCheck, Eye, Maximize2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const ORIGINS = ["Assam", "Dooars", "Darjeeling"];

function teaStages(p) {
  const dry = (p.dry_appearance || "").trim();
  const wet = (p.wet_appearance || "").trim();
  const liquor = (p.liquor_appearance || "").trim();
  if (dry || wet || liquor) return { dry, wet, liquor };
  const grade = (p.grade || "").trim();
  const origin = (p.origin || "").trim().toLowerCase();
  const dryByGrade = {
    Dust: "Fine, granular black dust — clean and uniform",
    PD: "Small, grainy black particles — even and clean",
    BP: "Bold, well-made black CTC granules — uniform",
    BOP: "Neat black CTC granules — good, even make",
    BOPSM: "Blackish, slightly tippy CTC — clean and uniform",
    OF: "Clean orange fannings — even, free from fibre, black",
    PF: "Fine black fannings — uniform",
  };
  const liquorByOrigin = {
    assam: "Bright golden-coppery — brisk and full-bodied with good strength",
    dooars: "Coppery-reddish — smooth and mellow with good colour",
    darjeeling: "Light, bright and clear — delicate and aromatic",
    terai: "Coppery — brisk with good colour",
    nilgiri: "Bright and golden — clean and fragrant",
  };
  return {
    dry: dryByGrade[grade] || "Clean, well-made black CTC — uniform",
    wet: "Coppery and bright — soft, fluffy infusion",
    liquor: liquorByOrigin[origin] || "Bright, coppery cup — brisk and full-bodied",
  };
}

function productCaption(p) {
  const g = gstInclusive(p.price, 5);
  const head = [`*${p.name}*`];
  if (p.grade) head.push(`Grade: ${p.grade}`);
  const lot = p.lot_no || p.product_number;
  const ref = [lot ? `Lot No: ${lot}` : "", p.invoice_no ? `Invoice No: ${p.invoice_no}` : ""].filter(Boolean).join("  |  ");
  if (ref) head.push(ref);
  if (p.origin) head.push(`Origin: ${p.origin}`);
  if (p.price && p.price_approved) head.push(`Price: ${p.price}${g ? ` (incl. 5% GST ${g.totalLabel})` : ""}`);
  else if (p.price) head.push("Price: on request");
  const s = teaStages(p);
  const notes = [];
  if (s.dry) notes.push(`*Dry Appearance:* ${s.dry}`);
  if (s.wet) notes.push(`*Wet Appearance (Infusion):* ${s.wet}`);
  if (s.liquor) notes.push(`*Liquor Appearance:* ${s.liquor}`);
  return [head.join("\n"), notes.join("\n")].filter(Boolean).join("\n\n");
}

export default function Products() {
  const { user } = useAuth();
  const { meta } = useMeta();
  const qc = useQueryClient();
  const isManager = user.role === "admin" || user.role === "manager";
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(null); // {mode, product}

  const [sort, setSort] = useState("price_asc");
  const [gradeFilter, setGradeFilter] = useState("all");
  const [selected, setSelected] = useState([]);
  const [pickerFor, setPickerFor] = useState(null);
  const [targetCustomer, setTargetCustomer] = useState(null);
  const [targetPickerOpen, setTargetPickerOpen] = useState(false);
  const [shareProducts, setShareProducts] = useState(null); // ShareDialog: array of products or null
  const [previewProduct, setPreviewProduct] = useState(null); // CardPreviewDialog: single product or null
  const navigate = useNavigate();

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products", search],
    queryFn: async () => (await api.get("/products", { params: { search, active_only: true } })).data,
  });

  const priceOf = (p) => { const x = parsePrice(p.price); return x ? x.num : null; };
  const sortItems = (items) => [...items].sort((a, b) => {
    if (sort === "newest") return (b.created_at || "").localeCompare(a.created_at || "");
    const na = priceOf(a), nb = priceOf(b);
    if (na == null && nb == null) return 0;
    if (na == null) return 1;
    if (nb == null) return -1;
    return sort === "price_desc" ? nb - na : na - nb;
  });
  const gradeList = meta?.tea_grades || [];
  const allGroups = [
    ...gradeList.map((g) => ({ key: g, label: g, items: products.filter((p) => p.grade === g) })),
    { key: "__ungraded__", label: "Ungraded", items: products.filter((p) => !gradeList.includes(p.grade)) },
  ].filter((grp) => grp.items.length > 0).map((grp) => ({ ...grp, items: sortItems(grp.items) }));
  const visibleGroups = gradeFilter === "all" ? allGroups : allGroups.filter((grp) => grp.key === gradeFilter);
  const visibleItems = visibleGroups.flatMap((grp) => grp.items);
  const allShownSelected = visibleItems.length > 0 && visibleItems.every((p) => selected.includes(p.id));
  const selectAllShown = () => {
    const ids = visibleItems.map((p) => p.id);
    setSelected((s) => (allShownSelected ? s.filter((id) => !ids.includes(id)) : Array.from(new Set([...s, ...ids]))));
  };

  const copyLink = (url) => { navigator.clipboard.writeText(url); toast.success("Image link copied"); };
  const openShare = (list) => { const items = (list || []).filter(Boolean); if (items.length) setShareProducts(items); };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? It will be removed from the library.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      qc.invalidateQueries({ queryKey: ["products"] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const selectedProducts = products.filter((p) => selected.includes(p.id));
  const selectAllInGroup = (items) => {
    const ids = items.map((p) => p.id);
    const allSel = ids.length > 0 && ids.every((id) => selected.includes(id));
    setSelected((s) => (allSel ? s.filter((id) => !ids.includes(id)) : Array.from(new Set([...s, ...ids]))));
  };

  return (
    <div className="animate-fade-up">
      <div className="mb-5">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900">Product image library</h1>
        <p className="mt-1 text-sm text-stone-500">Download or share product images &amp; PDF catalogues, then attach them manually in WhatsApp.</p>
      </div>

      <Tabs defaultValue="images" className="w-full">
        <TabsList data-testid="library-tabs">
          <TabsTrigger value="images" data-testid="tab-product-images">Product images</TabsTrigger>
          <TabsTrigger value="catalogues" data-testid="tab-catalogues">Catalogues (PDF)</TabsTrigger>
        </TabsList>

        <TabsContent value="images" className="mt-5">
          <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, number, garden, origin, grade" className="pl-9 bg-white" data-testid="product-search" />
            </div>
            <div className="flex items-center gap-2">
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="product-sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="price_asc">Price: Low to High</SelectItem>
                  <SelectItem value="price_desc">Price: High to Low</SelectItem>
                  <SelectItem value="newest">Newest first</SelectItem>
                </SelectContent>
              </Select>
              {isManager && (
                <Button className="bg-forest hover:bg-forest-600 text-ivory shrink-0" onClick={() => setDialog({ mode: "add" })} data-testid="add-product-button">
                  <Plus className="mr-1.5 h-4 w-4" /> Add product
                </Button>
              )}
            </div>
          </div>

          {!isLoading && products.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2" data-testid="grade-filter-bar">
              <button onClick={() => setGradeFilter("all")} data-testid="grade-filter-all" className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${gradeFilter === "all" ? "border-forest bg-forest text-ivory" : "border-stone-200 bg-white text-stone-600 hover:border-forest/40"}`}>All grades</button>
              {allGroups.map((grp) => (
                <button key={grp.key} onClick={() => setGradeFilter(grp.key)} data-testid={`grade-filter-${grp.key}`} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${gradeFilter === grp.key ? "border-forest bg-forest text-ivory" : "border-stone-200 bg-white text-stone-600 hover:border-forest/40"}`}>
                  {grp.key === "__ungraded__" ? "Ungraded" : `Grade ${grp.label}`} ({grp.items.length})
                </button>
              ))}
            </div>
          )}

          {!isLoading && visibleItems.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-3 text-xs text-stone-500" data-testid="select-all-shown-bar">
              <span>Showing {visibleItems.length} {visibleItems.length === 1 ? "tea" : "teas"}{gradeFilter !== "all" ? " in this grade" : ""}</span>
              <button onClick={selectAllShown} data-testid="select-all-shown" className="inline-flex items-center gap-1 rounded-full border border-stone-200 bg-white px-2.5 py-1 font-medium text-forest transition-colors hover:border-forest/40">
                <CheckCheck className="h-3 w-3" /> {allShownSelected ? "Deselect all shown" : "Select all shown"}
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>
          ) : visibleGroups.length === 0 ? (
            <div className="rounded-xl border border-dashed border-stone-300 bg-white/50 py-16 text-center" data-testid="products-empty">
              <Package className="mx-auto h-10 w-10 text-stone-300" />
              <p className="mt-3 text-sm text-stone-500">No products to show{gradeFilter !== "all" ? " for this grade" : ""}.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {visibleGroups.map((grp) => (
                <section key={grp.key} data-testid={`grade-section-${grp.key}`}>
                  <div className="mb-3 flex items-center gap-2">
                    <h2 className="font-heading text-lg font-semibold text-stone-900" data-testid={`grade-heading-${grp.key}`}>{grp.key === "__ungraded__" ? "Ungraded" : `Grade — ${grp.label}`}</h2>
                    <span className="rounded-full bg-forest/10 px-2 py-0.5 text-[11px] font-medium text-forest">{grp.items.length}</span>
                    <button onClick={() => selectAllInGroup(grp.items)} data-testid={`grade-select-all-${grp.key}`} className="ml-auto inline-flex items-center gap-1 rounded-full border border-stone-200 bg-white px-2.5 py-1 text-[11px] font-medium text-forest transition-colors hover:border-forest/40">
                      <CheckCheck className="h-3 w-3" /> {grp.items.every((p) => selected.includes(p.id)) ? "Deselect all" : "Select all"}
                    </button>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {grp.items.map((p) => (
                      <ProductCard key={p.id} p={p} isManager={isManager} selected={selected.includes(p.id)} onToggleSelect={() => toggleSelect(p.id)} onSend={() => setPickerFor(p)} onCopy={copyLink} onShare={(pp) => openShare([pp])} onDelete={del} onEdit={(pp) => setDialog({ mode: "edit", product: pp })} onView={(pp) => setPreviewProduct(pp)} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="catalogues" className="mt-5">
          <CataloguesSection />
        </TabsContent>
      </Tabs>

      {selected.length > 0 && (
        <div className="fixed inset-x-0 bottom-4 z-40 mx-auto flex w-[min(760px,94%)] flex-col gap-2 rounded-2xl border border-stone-200 bg-white px-4 py-3 shadow-lg sm:flex-row sm:items-center sm:justify-between" data-testid="multiselect-bar">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-stone-700">{selected.length} selected</span>
              <button onClick={() => setTargetPickerOpen(true)} className="inline-flex items-center gap-1 rounded-full border border-stone-200 px-2 py-0.5 text-[11px] text-stone-600 hover:border-forest/40" data-testid="multiselect-choose-customer"><User className="h-3 w-3" /> {targetCustomer ? `To: ${targetCustomer.name}` : "To: choose customer"}</button>
              {targetCustomer && <button onClick={() => setTargetCustomer(null)} className="text-[11px] text-stone-400 hover:text-red-500" data-testid="multiselect-clear-customer">clear</button>}
            </div>
            <span className="hidden text-[11px] text-stone-400 sm:inline">Sent one at a time — each photo goes with its full details in a single WhatsApp message{targetCustomer ? ` — pick ${targetCustomer.name}'s chat each time` : ""}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" className="bg-whatsapp hover:bg-whatsapp/90 text-white" onClick={() => openShare(selectedProducts)} data-testid="multiselect-share"><ImageIcon className="mr-1 h-3.5 w-3.5" /> Share image + details</Button>
            <Button size="sm" variant="ghost" className="text-stone-500" onClick={() => { setSelected([]); setTargetCustomer(null); }} data-testid="multiselect-clear">Clear</Button>
          </div>
        </div>
      )}

      {pickerFor && <CustomerPickerDialog title={`Send "${pickerFor.name}" to…`} subtitle="Pick a customer — we'll open their Communication Copilot with this product ready to go." onClose={() => setPickerFor(null)} onPick={(cust) => { const pid = pickerFor.id; setPickerFor(null); navigate(`/customers/${cust.id}?product=${pid}`); }} />}

      {targetPickerOpen && <CustomerPickerDialog title="Send to which customer?" subtitle="The price list will open directly in this customer's WhatsApp chat." onClose={() => setTargetPickerOpen(false)} onPick={(c) => { setTargetCustomer(c); setTargetPickerOpen(false); }} />}

      {shareProducts && <ShareDialog products={shareProducts} targetCustomer={shareProducts.length > 1 ? targetCustomer : null} onClose={() => setShareProducts(null)} />}

      {previewProduct && <CardPreviewDialog p={previewProduct} onClose={() => setPreviewProduct(null)} />}

      {dialog && <ProductDialog dialog={dialog} onClose={() => setDialog(null)} meta={meta} onSaved={() => { qc.invalidateQueries({ queryKey: ["products"] }); setDialog(null); }} />}
    </div>
  );
}

function PriceLine({ p }) {
  if (!p.price) return null;
  const g = gstInclusive(p.price, 5);
  return (
    <div className="mt-1 text-sm font-semibold text-forest" data-testid={`product-price-${p.id}`}>
      {p.price}
      {g && <span className="ml-1 text-[11px] font-normal text-stone-500">· incl. 5% GST {g.totalLabel}</span>}
      {p.price_approved
        ? <span className="ml-1 text-[10px] font-normal text-stone-400">valid {fmtDate(p.price_valid_until)}</span>
        : <span className="ml-1 text-[10px] font-normal text-red-500">not approved to share</span>}
    </div>
  );
}

function ProductCard({ p, isManager, selected, onToggleSelect, onSend, onCopy, onShare, onDelete, onEdit, onView }) {
  const lot = p.lot_no || p.product_number;
  const idLine = [lot ? `Lot ${lot}` : "", p.invoice_no ? `Inv ${p.invoice_no}` : ""].filter(Boolean).join(" · ");
  const [zoom, setZoom] = useState(false);
  return (
    <div className={`overflow-hidden rounded-xl border bg-white ${selected ? "border-forest ring-1 ring-forest" : "border-stone-200"}`} data-testid={`product-card-${p.id}`}>
      <div className="group relative aspect-[4/3] w-full overflow-hidden bg-stone-100">
        {p.image_url ? (
          <>
            <img src={p.image_url} alt={p.name} className="h-full w-full cursor-zoom-in object-cover" onClick={() => setZoom(true)} data-testid={`product-image-${p.id}`} />
            <button onClick={() => setZoom(true)} className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-black/55 px-1.5 py-1 text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100" data-testid={`product-expand-${p.id}`}>
              <Maximize2 className="h-3 w-3" /> View full
            </button>
          </>
        ) : <div className="grid h-full place-items-center text-stone-300"><Package className="h-10 w-10" /></div>}
        <label className="absolute left-2 top-2 inline-flex cursor-pointer items-center rounded-md bg-white/90 p-1 shadow-sm">
          <input type="checkbox" checked={selected} onChange={onToggleSelect} className="h-4 w-4 accent-[#1f4d3a]" data-testid={`product-select-${p.id}`} />
        </label>
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-heading font-semibold text-stone-900 truncate">{p.name}</div>
            {idLine && <div className="text-[11px] text-stone-400">{idLine}</div>}
          </div>
          {p.grade && <Badge variant="outline" className="text-[10px]">{p.grade}</Badge>}
        </div>
        <div className="mt-1 text-xs text-stone-500">{[p.garden, p.origin].filter(Boolean).join(" · ")}</div>
        <PriceLine p={p} />
        {p.description && <p className="mt-1 text-xs text-stone-500 line-clamp-2">{p.description}</p>}
        {!p.description && <p className="mt-1 text-xs text-stone-500 line-clamp-2">{teaStages(p).liquor}</p>}
        <Button size="sm" variant="outline" className="mt-3 w-full border-forest/40 text-forest hover:bg-forest/5" onClick={() => onView(p)} data-testid={`product-view-${p.id}`}><Eye className="mr-1.5 h-3.5 w-3.5" /> View card</Button>
        <Button size="sm" className="mt-2 w-full bg-whatsapp hover:bg-whatsapp/90 text-white" onClick={() => onShare(p)} data-testid={`product-share-${p.id}`}><Share2 className="mr-1.5 h-3.5 w-3.5" /> Share on WhatsApp</Button>
        <Button size="sm" variant="outline" className="mt-2 w-full" onClick={onSend} data-testid={`product-send-${p.id}`}><Send className="mr-1.5 h-3.5 w-3.5" /> Send to customer</Button>
        <div className="mt-2 flex gap-1.5">
          <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => onCopy(p.image_url)} data-testid={`product-copy-${p.id}`}><Copy className="mr-1 h-3.5 w-3.5" /> Link</Button>
          <a href={p.image_url} download target="_blank" rel="noreferrer" className="flex-1"><Button size="sm" variant="outline" className="w-full text-xs" data-testid={`product-download-${p.id}`}><Download className="mr-1 h-3.5 w-3.5" /> Save</Button></a>
          {isManager && <Button size="sm" variant="ghost" className="text-xs" onClick={() => onEdit(p)} data-testid={`product-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
          {isManager && <Button size="sm" variant="ghost" className="text-xs text-red-500 hover:text-red-600" onClick={() => onDelete(p)} data-testid={`product-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
        </div>
      </div>
      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="max-w-5xl border-0 bg-black/95 p-2" data-testid={`product-lightbox-${p.id}`}>
          <DialogHeader className="sr-only"><DialogTitle>{p.name}</DialogTitle></DialogHeader>
          <img src={p.image_url} alt={p.name} className="mx-auto max-h-[85vh] w-auto max-w-full object-contain" data-testid={`product-lightbox-image-${p.id}`} />
          <p className="pb-1 pt-2 text-center text-[11px] text-white/70">Original full-resolution image</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProductDialog({ dialog, onClose, meta, onSaved }) {
  const editing = dialog.mode === "edit";
  const p = dialog.product || {};
  const [form, setForm] = useState({
    name: p.name || "", lot_no: p.lot_no || p.product_number || "", invoice_no: p.invoice_no || "", garden: p.garden || "",
    origin: p.origin || "", grade: p.grade || "", price: p.price || "",
    price_approved: p.price_approved || false, price_valid_until: p.price_valid_until || "",
    image_url: p.image_url || "", description: p.description || "", stock_status: p.stock_status || "In stock",
    dry_appearance: p.dry_appearance || "", wet_appearance: p.wet_appearance || "", liquor_appearance: p.liquor_appearance || "",
    active: p.active !== false,
  });
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scanModel, setScanModel] = useState("claude");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onScan = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setScanning(true);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("model", scanModel);
      const r = await api.post("/products/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data || {};
      setForm((prev) => ({
        ...prev,
        name: d.name || d.brand || prev.name,
        lot_no: d.product_number || d.lot_no || prev.lot_no,
        garden: d.garden || d.brand || prev.garden,
        origin: d.origin || prev.origin,
        grade: (meta?.tea_grades || []).includes(d.grade) ? d.grade : prev.grade,
        dry_appearance: d.dry_appearance || prev.dry_appearance,
        wet_appearance: d.wet_appearance || prev.wet_appearance,
        liquor_appearance: d.liquor_appearance || prev.liquor_appearance,
        image_url: d.image_url ? `${process.env.REACT_APP_BACKEND_URL}${d.image_url}` : prev.image_url,
      }));
      toast.success("Scanned — please review the details before saving");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setScanning(false); }
  };

  const onUploadImage = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const preview = async () => {
    if (!form.name.trim()) { toast.error("Add a product name to preview the card"); return; }
    setPreviewing(true);
    try {
      const r = await api.post("/products/card-preview", form, { responseType: "blob" });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(r.data));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not build the card preview");
    } finally { setPreviewing(false); }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Product name is required"); return; }
    if (!(form.origin || "").trim()) { toast.error("Origin is required — pick Assam, Dooars or Darjeeling so it shows on the card"); return; }
    setLoading(true);    try {
      if (editing) await api.patch(`/products/${p.id}`, form);
      else await api.post("/products", form);
      toast.success(editing ? "Product updated" : "Product added");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const removeProduct = async () => {
    if (!window.confirm(`Delete "${p.name}"? It will be removed from the library.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <>
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Edit product" : "Add product"}</DialogTitle>
          <DialogDescription>Scan a tea packet to auto-fill the details, or enter them manually. Review before saving.</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-gold/40 bg-gold-bg/40 p-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-stone-700"><Sparkles className="h-4 w-4 text-gold" /> Auto-fill from a tea packet photo</div>
          <p className="mt-0.5 text-xs text-stone-500">Upload a clear photo of the sample packet / label — AI reads the grade, lot, garden &amp; more. You can edit everything afterwards.</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <label className={`inline-flex items-center gap-1.5 rounded-md bg-gold px-3 py-1.5 text-xs font-semibold text-white hover:bg-gold-bright ${scanning ? "cursor-wait opacity-80" : "cursor-pointer"}`}>
              {scanning ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading packet…</> : <><Camera className="h-3.5 w-3.5" /> Scan tea packet</>}
              <input type="file" accept="image/*" className="hidden" onChange={onScan} disabled={scanning} data-testid="product-scan-input" />
            </label>
            <div className="flex items-center gap-1 text-xs text-stone-500">
              <span>with</span>
              <Select value={scanModel} onValueChange={setScanModel}>
                <SelectTrigger className="h-8 w-auto min-w-[140px] text-xs" data-testid="product-scan-model-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.ai_models || []).map((m) => (
                    <SelectItem key={m.key} value={m.key} data-testid={`product-scan-model-${m.key}`}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Fld label="Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name-input" /></Fld>
          <Fld label="Lot no."><Input value={form.lot_no} onChange={(e) => set("lot_no", e.target.value)} data-testid="product-lot-input" /></Fld>
          <Fld label="Invoice no."><Input value={form.invoice_no} onChange={(e) => set("invoice_no", e.target.value)} data-testid="product-invoice-input" /></Fld>
          <Fld label="Garden / manufacturer"><Input value={form.garden} onChange={(e) => set("garden", e.target.value)} /></Fld>
          <Fld label="Origin *">
            <Select value={form.origin || "none"} onValueChange={(v) => set("origin", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="product-origin-select"><SelectValue placeholder="Origin" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {ORIGINS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                {form.origin && !ORIGINS.includes(form.origin) && <SelectItem value={form.origin}>{form.origin}</SelectItem>}
              </SelectContent>
            </Select>
          </Fld>
          <Fld label="Grade">
            <Select value={form.grade || "none"} onValueChange={(v) => set("grade", v === "none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Grade" /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{(meta?.tea_grades || []).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </Fld>
          <Fld label="Stock status"><Input value={form.stock_status} onChange={(e) => set("stock_status", e.target.value)} /></Fld>
          <Fld label="Price">
            <Input value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="₹245/kg" data-testid="product-price-input" />
            {gstInclusive(form.price, 5) && <p className="text-[11px] text-stone-500" data-testid="product-price-gst">Incl. 5% GST: {gstInclusive(form.price, 5).totalLabel}</p>}
          </Fld>
          <Fld label="Price valid until"><Input type="date" value={form.price_valid_until ? form.price_valid_until.slice(0,10) : ""} onChange={(e) => set("price_valid_until", e.target.value)} /></Fld>
        </div>
        <Fld label="Product image">
          <div className="flex items-center gap-3">
            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-stone-200 bg-stone-100">
              {form.image_url ? <img src={form.image_url} alt="" className="h-full w-full object-cover" data-testid="product-image-preview" /> : <div className="grid h-full place-items-center text-stone-300"><ImageIcon className="h-6 w-6" /></div>}
            </div>
            <div className="min-w-0 flex-1 space-y-1.5">
              <label className={`inline-flex items-center gap-1.5 rounded-md border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-stone-600 hover:border-forest/40 ${uploading ? "cursor-wait opacity-70" : "cursor-pointer"}`}>
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />} Upload image
                <input type="file" accept="image/*" className="hidden" onChange={onUploadImage} disabled={uploading} data-testid="product-image-upload" />
              </label>
              <Input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="or paste an image URL" className="h-8 text-xs" data-testid="product-image-input" />
            </div>
          </div>
        </Fld>
        <div className="rounded-lg border border-stone-200 bg-stone-50/60 p-3">
          <div className="mb-2 text-xs font-semibold text-stone-600">Tasting notes (three stages)</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Fld label="Dry Appearance"><Textarea value={form.dry_appearance} onChange={(e) => set("dry_appearance", e.target.value)} rows={2} placeholder="e.g. Clean OF, even, black" data-testid="product-dry-input" /></Fld>
            <Fld label="Wet Appearance (Infusion)"><Textarea value={form.wet_appearance} onChange={(e) => set("wet_appearance", e.target.value)} rows={2} placeholder="e.g. Coppery, bright, fluffy" data-testid="product-wet-input" /></Fld>
            <Fld label="Liquor Appearance"><Textarea value={form.liquor_appearance} onChange={(e) => set("liquor_appearance", e.target.value)} rows={2} placeholder="e.g. Golden-coppery, brisk" data-testid="product-liquor-input" /></Fld>
          </div>
          <p className="mt-1.5 text-[11px] text-stone-400">Leave blank to auto-generate a note from grade &amp; origin.</p>
        </div>
        <div className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2">
          <span className="text-sm text-stone-600">Price approved for sharing</span>
          <Switch checked={form.price_approved} onCheckedChange={(v) => set("price_approved", v)} data-testid="product-price-approved" />
        </div>
        <DialogFooter className="sm:justify-between">
          {editing ? (
            <Button variant="outline" className="text-red-500 hover:text-red-600" onClick={removeProduct} data-testid="product-dialog-delete"><Trash2 className="mr-1.5 h-4 w-4" /> Delete</Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={preview} disabled={previewing || loading} data-testid="product-preview-btn">
              {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Eye className="mr-1.5 h-4 w-4" /> Preview card</>}
            </Button>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={save} disabled={loading} data-testid="product-save">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={!!previewUrl} onOpenChange={(v) => { if (!v) closePreview(); }}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden" data-testid="product-preview-dialog">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="font-heading flex items-center gap-2"><Eye className="h-4 w-4 text-gold" /> Card preview</DialogTitle>
          <DialogDescription>This is the image customers receive on WhatsApp. Close to keep editing, then Save.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-auto bg-stone-100 p-3">
          {previewUrl && <img src={previewUrl} alt="Card preview" className="w-full rounded-lg border border-stone-200" data-testid="product-preview-image" />}
        </div>
        <div className="p-3">
          <Button variant="outline" className="w-full" onClick={closePreview} data-testid="product-preview-close">Close preview</Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

function Fld({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs font-semibold text-stone-500">{label}</Label>{children}</div>;
}

function CardPreviewDialog({ p, onClose }) {
  const [busy, setBusy] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const base = process.env.REACT_APP_BACKEND_URL;
  const cardUrl = useMemo(() => `${base}/api/products/${p.id}/share-card?v=${Date.now()}`, [base, p.id]);
  const safeName = (p.name || "tea").replace(/[^A-Za-z0-9]+/g, "_");

  const share = async () => {
    setBusy("img");
    try {
      const r = await shareImages([cardUrl], null);
      if (r === "cancelled") return;
      if (r === "downloaded") toast("Card image saved — attach it in WhatsApp (photo + all details are in the one image)");
      else if (r === "shared") toast.success("Ready to share");
    } finally { setBusy(null); }
  };
  const sharePdfNow = async () => {
    setBusy("pdf");
    try {
      const r = await sharePdf(`${base}/api/products/${p.id}/spec-sheet?v=${Date.now()}`, safeName);
      if (r === "cancelled") return;
      if (r === "downloaded") toast("Spec sheet PDF saved — attach it in WhatsApp");
      else if (r === "shared") toast.success("Ready to share PDF");
    } finally { setBusy(null); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden" data-testid="card-preview-dialog">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="font-heading">{p.name} — product card</DialogTitle>
          <DialogDescription>
            This exact image — the photo with the full report in one picture — is what your customer receives on WhatsApp.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[58vh] overflow-y-auto bg-stone-100 px-5 py-4">
          {!loaded && <div className="grid h-72 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>}
          <img
            src={cardUrl}
            alt={`${p.name} product card`}
            onLoad={() => setLoaded(true)}
            className={`mx-auto w-full rounded-lg border border-stone-200 shadow-sm ${loaded ? "" : "hidden"}`}
            data-testid="card-preview-image"
          />
        </div>
        <DialogFooter className="flex flex-col gap-2 px-5 pb-5 sm:flex-col sm:space-x-0">
          <Button className="w-full bg-whatsapp hover:bg-whatsapp/90 text-white" disabled={!!busy} onClick={share} data-testid="card-preview-share">
            {busy === "img" ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Preparing…</> : <><Share2 className="mr-1.5 h-4 w-4" /> Share on WhatsApp</>}
          </Button>
          <Button variant="outline" className="w-full" disabled={!!busy} onClick={sharePdfNow} data-testid="card-preview-pdf">
            {busy === "pdf" ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> PDF…</> : <><FileText className="mr-1.5 h-4 w-4" /> Send as PDF</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShareDialog({ products, targetCustomer, onClose }) {
  const [sent, setSent] = useState([]);
  const [openReport, setOpenReport] = useState(null);
  const [busy, setBusy] = useState(null);
  const [busyPdf, setBusyPdf] = useState(null);
  const many = products.length > 1;
  const sendOne = async (p) => {
    if (!p.image_url) { toast.error("This tea has no product image yet — add one before sharing"); return; }
    setBusy(p.id);
    try {
      const cardUrl = `${process.env.REACT_APP_BACKEND_URL}/api/products/${p.id}/share-card?v=${Date.now()}`;
      const r = await shareImages([cardUrl], null);
      if (r === "cancelled") return;
      setSent((s) => (s.includes(p.id) ? s : [...s, p.id]));
      if (r === "downloaded") toast("Card image saved — attach it in WhatsApp (the photo + all details are in the one image)");
      else if (r === "shared") toast.success("Ready to share");
    } finally {
      setBusy(null);
    }
  };
  const sendPdf = async (p) => {
    if (!p.image_url) { toast.error("This tea has no product image yet — add one before sharing"); return; }
    setBusyPdf(p.id);
    try {
      const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/products/${p.id}/spec-sheet?v=${Date.now()}`;
      const r = await sharePdf(pdfUrl, (p.name || "tea").replace(/[^A-Za-z0-9]+/g, "_"));
      if (r === "cancelled") return;
      setSent((s) => (s.includes(p.id) ? s : [...s, p.id]));
      if (r === "downloaded") toast("Spec sheet PDF saved — attach it in WhatsApp");
      else if (r === "shared") toast.success("Ready to share PDF");
    } finally {
      setBusyPdf(null);
    }
  };
  const copyAll = async () => {
    const text = `*CTC TEA LIST*\n\n` + products.map((p, i) => `${i + 1}. ${productCaption(p)}`).join("\n\n");
    try { await navigator.clipboard.writeText(text); toast.success("Full price list copied"); } catch { toast.error("Copy failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">{many ? "Share image + details" : "Share on WhatsApp"}</DialogTitle>
          <DialogDescription>
            Each tea is shared as a single card image — the photo with all its details (grade, lot, origin, price and tasting notes) rendered inside one picture, so it always arrives as ONE WhatsApp message on phone and computer. Prefer a formal document? Use <strong>Send as PDF</strong> to share a one-page spec sheet instead.
            {many ? " One tea at a time — tap Send on each and pick the same WhatsApp chat each time." : ""}
            {targetCustomer ? ` Sending to ${targetCustomer.name}.` : ""}
          </DialogDescription>
        </DialogHeader>
        {many && <div className="text-xs font-medium text-stone-500" data-testid="share-progress">Sent {sent.length} of {products.length}</div>}
        <div className="max-h-[24rem] space-y-2 overflow-auto">
          {products.map((p) => {
            const isSent = sent.includes(p.id);
            const showReport = openReport === p.id;
            return (
              <div key={p.id} className="rounded-lg border border-stone-100 p-2.5" data-testid={`share-row-${p.id}`}>
                <div className="flex items-center gap-3">
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md bg-stone-100">
                    {p.image_url ? <img src={p.image_url} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-stone-300"><Package className="h-5 w-5" /></div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-stone-800">{p.name}{p.grade ? ` · ${p.grade}` : ""}</div>
                    <div className="truncate text-[11px] text-stone-400">{p.price && p.price_approved ? p.price : "Price on request"}</div>
                    <button onClick={() => setOpenReport(showReport ? null : p.id)} className="mt-0.5 inline-flex items-center gap-0.5 text-[11px] font-medium text-forest hover:underline" data-testid={`share-report-toggle-${p.id}`}>
                      <ChevronDown className={`h-3 w-3 transition-transform ${showReport ? "rotate-180" : ""}`} /> {showReport ? "Hide details" : "Preview details"}
                    </button>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button size="sm" variant={isSent ? "outline" : "default"} disabled={busy === p.id || busyPdf === p.id} className={isSent ? "border-forest text-forest" : "bg-whatsapp hover:bg-whatsapp/90 text-white"} onClick={() => sendOne(p)} data-testid={`share-send-${p.id}`}>
                      {busy === p.id ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Preparing…</> : isSent ? <><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Sent</> : <><Send className="mr-1 h-3.5 w-3.5" /> Send image</>}
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy === p.id || busyPdf === p.id} className="text-stone-600 hover:text-forest" onClick={() => sendPdf(p)} data-testid={`share-pdf-${p.id}`}>
                      {busyPdf === p.id ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> PDF…</> : <><FileText className="mr-1 h-3.5 w-3.5" /> Send as PDF</>}
                    </Button>
                  </div>
                </div>
                {showReport && <pre className="mt-2 whitespace-pre-wrap rounded-md bg-stone-50 p-2 text-[11px] leading-relaxed text-stone-600" data-testid={`share-report-${p.id}`}>{productCaption(p)}</pre>}
              </div>
            );
          })}
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          {many ? <Button variant="outline" size="sm" onClick={copyAll} data-testid="share-copy-list"><FileText className="mr-1 h-3.5 w-3.5" /> Copy all as text</Button> : <span />}
          <Button variant="ghost" onClick={onClose} data-testid="share-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomerPickerDialog({ title, subtitle, onClose, onPick }) {
  const [q, setQ] = useState("");
  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["customers", "picker", q],
    queryFn: async () => (await api.get("/customers", { params: { search: q, status: "active" } })).data,
  });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers by name, company, number" className="pl-9" data-testid="picker-search" />
        </div>
        <div className="mt-1 max-h-72 space-y-1 overflow-auto">
          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-forest" /></div>
          ) : customers.length === 0 ? (
            <p className="py-8 text-center text-sm text-stone-400">No customers found</p>
          ) : customers.map((c) => (
            <button key={c.id} onClick={() => onPick(c)} data-testid={`picker-customer-${c.id}`} className="flex w-full items-center justify-between gap-2 rounded-lg border border-stone-100 px-3 py-2 text-left hover:border-forest/40 hover:bg-forest/5">
              <div className="min-w-0">
                <div className="truncate font-medium text-stone-800">{c.name}</div>
                <div className="truncate text-xs text-stone-400">{[c.company, c.mobile].filter(Boolean).join(" · ")}</div>
              </div>
              <Badge variant="outline" className="shrink-0 text-[10px]">{c.stage_label || c.stage}</Badge>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function catalogueUrl(id) {
  return `${process.env.REACT_APP_BACKEND_URL}/api/catalogues/${id}/file`;
}

function CataloguesSection() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);

  const { data: catalogues = [], isLoading } = useQuery({
    queryKey: ["catalogues"],
    queryFn: async () => (await api.get("/catalogues")).data,
  });

  const copyLink = (c) => { navigator.clipboard.writeText(catalogueUrl(c.id)); toast.success("Catalogue link copied"); };
  const share = async (c) => {
    const url = catalogueUrl(c.id);
    if (navigator.share) { try { await navigator.share({ title: c.title, text: c.title, url }); } catch {} }
    else copyLink(c);
  };
  const remove = async (c) => {
    if (!window.confirm(`Remove "${c.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/catalogues/${c.id}`);
      toast.success("Catalogue removed");
      qc.invalidateQueries({ queryKey: ["catalogues"] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const canDelete = (c) => user.role === "admin" || user.role === "manager" || c.uploaded_by_id === user.id;

  return (
    <div>
      <div className="mb-5 flex items-center justify-between gap-3">
        <p className="text-sm text-stone-500">{catalogues.length} catalogue(s). Upload a PDF once — every salesperson can attach it in WhatsApp.</p>
        <Button className="bg-forest hover:bg-forest-600 text-ivory shrink-0" onClick={() => setUploadOpen(true)} data-testid="upload-catalogue-button">
          <UploadCloud className="mr-1.5 h-4 w-4" /> Upload PDF
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>
      ) : catalogues.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white/50 py-16 text-center" data-testid="catalogues-empty">
          <FileText className="mx-auto h-10 w-10 text-stone-300" />
          <p className="mt-3 text-sm text-stone-500">No catalogues yet. Upload your product catalogue PDF to share it on WhatsApp.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {catalogues.map((c) => (
            <div key={c.id} className="rounded-xl border border-stone-200 bg-white p-4" data-testid={`catalogue-card-${c.id}`}>
              <div className="flex items-start gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-red-50 text-red-500"><FileText className="h-5 w-5" /></span>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-semibold text-stone-900 truncate" title={c.title}>{c.title}</div>
                  <div className="text-[11px] text-stone-400 truncate">{c.original_filename} · {fmtSize(c.size)}</div>
                  <div className="text-[11px] text-stone-400">Added by {c.uploaded_by} · {fmtDate(c.created_at)}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-1.5">
                <a href={catalogueUrl(c.id)} target="_blank" rel="noreferrer" className="col-span-2"><Button size="sm" variant="outline" className="w-full text-xs" data-testid={`catalogue-open-${c.id}`}><ExternalLink className="mr-1 h-3.5 w-3.5" /> Open PDF</Button></a>
                <Button size="sm" variant="outline" className="text-xs" onClick={() => copyLink(c)} data-testid={`catalogue-copy-${c.id}`}><Copy className="mr-1 h-3.5 w-3.5" /> Link</Button>
                <Button size="sm" variant="outline" className="text-xs" onClick={() => share(c)} data-testid={`catalogue-share-${c.id}`}><Share2 className="mr-1 h-3.5 w-3.5" /> Share</Button>
                {canDelete(c) && <Button size="sm" variant="ghost" className="col-span-2 text-xs text-red-500 hover:text-red-600" onClick={() => remove(c)} data-testid={`catalogue-delete-${c.id}`}><Trash2 className="mr-1 h-3.5 w-3.5" /> Remove</Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <UploadCatalogueDialog open={uploadOpen} onOpenChange={setUploadOpen} onDone={() => qc.invalidateQueries({ queryKey: ["catalogues"] })} />
    </div>
  );
}

function UploadCatalogueDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setFile(null); setTitle(""); };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f && f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please choose a PDF file");
      return;
    }
    setFile(f || null);
    if (f && !title) setTitle(f.name.replace(/\.pdf$/i, ""));
  };

  const upload = async () => {
    if (!file) { toast.error("Please choose a PDF file"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title || file.name);
      await api.post("/catalogues", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Catalogue uploaded");
      onDone?.();
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><FileText className="h-5 w-5 text-forest" /> Upload catalogue (PDF)</DialogTitle>
          <DialogDescription>Upload a PDF product catalogue. Everyone on the team can then download, share or attach it in WhatsApp.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Catalogue name</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Prime Gold Tea — 2026 Catalogue" data-testid="catalogue-title-input" />
          </div>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-stone-300 bg-stone-50 py-8 text-center hover:border-forest/40">
            <UploadCloud className="h-7 w-7 text-stone-400" />
            <span className="text-sm text-stone-600">{file ? file.name : "Click to choose a PDF file"}</span>
            <span className="text-[11px] text-stone-400">PDF only · max 25 MB</span>
            <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={onFile} data-testid="catalogue-file-input" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={upload} disabled={loading || !file} data-testid="catalogue-upload-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
